/**
 * Handing the browser a file (issue #304).
 *
 * Two lines of DOM in their own module, because they are the only place in this app that
 * reaches for `document` outside React, and because the reason they are safe is worth writing
 * down once rather than being re-derived at the call site.
 *
 * **An object URL, not a `data:` URL.** A family's archive can be a megabyte or two, and a
 * `data:` href of that size is a URL long enough to be refused by some browsers and truncated
 * in others — a failure that shows up as a corrupt file rather than as an error. The object URL
 * is revoked immediately after the click: the blob is already handed to the download by then,
 * so holding it any longer would only keep the bytes alive in the tab.
 *
 * **The anchor is never in the document.** Appending it and removing it again is the usual
 * recipe and is not needed — a click on a detached anchor starts a download in every browser
 * this app supports — and not appending it means there is no moment where a stray element with
 * a family's data on it exists in the page.
 *
 * **This host's CSP is `default-src 'none'` (`my.tf`), and no directive governs a download.**
 * That is the question a reviewer should ask and it is worth answering here rather than in a
 * commit message: a `<a download>` click is a *download*, not a fetch and not a navigation, so
 * `connect-src` and `frame-src` never see it, `navigate-to` was dropped from the spec and
 * shipped nowhere, and there is no `sandbox` directive on this policy to disable downloads.
 * Nothing about the blob needs adding to the policy — which is also why `blob:` appears in no
 * directive and must not be added "to be safe", since that would widen a policy for a request
 * that is never made.
 */

/** Hand `contents` to the browser as a download called `name`. */
export function downloadFile(name: string, contents: string, type: string): void {
  const url = URL.createObjectURL(new Blob([contents], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.rel = "noopener";
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * Put `text` on the clipboard, answering whether it got there.
 *
 * **The result is returned rather than assumed**, which is the whole reason this is a function.
 * `navigator.clipboard` is absent outside a secure context and the write can be refused by
 * permissions policy, and a *Copied* label shown over a clipboard that still holds something
 * else is the kind of small lie this surface cannot afford: a family who copies their record,
 * closes the panel and pastes gets somebody else's text with no way to know why.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
